import moment from 'moment';

/**
 * Get property options from properties object
 */
export const getPropertyOptions = (properties) => {
    let options = [];
    for (let property of Object.values(properties)) {
        options.push({ id: property.propertyId, label: property.propertyName });
    }
    return options;
};

/**
 * Convert property IDs array to string array
 */
export const getSelectedPropertyIds = (properties) => {
    return properties.map(p => String(p));
};

/**
 * Filter preview invoices by search text and property
 */
export const getFilteredPreviewInvoices = (previewInvoices, searchText, selectedPropertyId) => {
    let filtered = [...previewInvoices];

    if (searchText.trim()) {
        const searchLower = searchText.toLowerCase().trim();
        filtered = filtered.filter(inv =>
            inv.tenantName.toLowerCase().includes(searchLower)
        );
    }

    if (selectedPropertyId) {
        filtered = filtered.filter(inv => String(inv.propertyId) === String(selectedPropertyId));
    }

    return filtered;
};

/**
 * Get unique properties from preview invoices
 */
export const getUniquePropertiesFromPreview = (previewInvoices) => {
    const properties = new Set();
    previewInvoices.forEach(inv => {
        if (inv.propertyId) {
            properties.add(inv.propertyId);
        }
    });
    return Array.from(properties).map(propertyId => {
        const inv = previewInvoices.find(i => String(i.propertyId) === String(propertyId));
        return {
            id: propertyId,
            label: inv ? inv.propertyName : `Property ${propertyId}`
        };
    });
};

/**
 * Get month range from date string (YYYY-MM-DD)
 */
export const getMonthRange = (dateStr) => {
    const [y, m] = dateStr.split('-').map(x => parseInt(x, 10));
    const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
    const endDate = new Date(y, m, 0);
    const end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    return { startDate, endDate: end };
};

/**
 * Transform calculated invoices to preview format
 */
export const transformCalculatedInvoices = (calculated) => {
    return calculated.map(row => ({
        tenantId: row.tenantId,
        tenantName: row.tenantName,
        propertyId: row.propertyId,
        propertyName: row.propertyName,
        unitName: row.unitName,
        amount: (row.invoiceItems.find(i => i.name.toLowerCase() === 'rent')?.amount) || 0,
        invoiceDate: row.invoiceDate,
        items: row.invoiceItems,
        isValid: (typeof row.isValid === 'boolean')
            ? row.isValid
            : !((row.invoiceItems || []).some(it => (typeof it.isValid === 'boolean' ? !it.isValid : (it.errors || []).length > 0)))
    }));
};

/**
 * Build duplicate warnings map from existing invoices
 */
export const buildDuplicateWarningsMap = (invoices) => {
    const duplicateMap = {};
    for (const inv of Object.values(invoices)) {
        const t = inv.tenant || {};
        if (inv.item?.toLowerCase().includes('rent')) {
            duplicateMap[String(t.tenantId)] = 'Warning: Rent invoice already exists for this month';
        }
    }
    return duplicateMap;
};

/**
 * Build invoice payload from preview invoices
 */
export const buildInvoicePayload = (filteredInvoices) => {
    const validRows = filteredInvoices.filter(inv => {
        const rowValid = (typeof inv.isValid === 'boolean')
            ? inv.isValid
            : !((inv.items || []).some(it => (typeof it.isValid === 'boolean' ? !it.isValid : (it.errors || []).length > 0)));
        return rowValid;
    });
    
    const invoicesPayload = [];
    for (const inv of validRows) {
        const validItems = (inv.items || []).filter(item =>
            (typeof item.isValid === 'boolean') ? item.isValid : ((item.errors || []).length === 0)
        );
        if (validItems.length === 0) {
            continue;
        }
        invoicesPayload.push({
            tenantId: inv.tenantId,
            status: 'open',
            invoiceItems: validItems.map(item => ({ 
                name: [item.name], 
                amount: item.amount, 
                description: '',
                isValid: true
            })),
            description: '',
            invoiceDate: inv.invoiceDate
        });
    }
    
    return invoicesPayload;
};

/**
 * Transform invoice payload to createBulkInvoices format
 */
export const transformInvoiceInputs = (invoicesPayload) => {
    return invoicesPayload.map(inv => ({
        tenantId: inv.tenantId,
        invoiceDate: inv.invoiceDate,
        invoiceItems: inv.invoiceItems.map(ii => ({
            name: Array.isArray(ii.name) ? ii.name[0] : ii.name,
            amount: ii.amount,
            isValid: true
        }))
    }));
};

